import React, { useState } from 'react';
import { FileText, AlertCircle, CheckCircle2, Loader2, Send, RefreshCw, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { apiCall, default as API_CONFIG } from '../config/api';
import { useTranscripts, invalidateTranscriptsCache } from '../hooks/useTranscripts';
import { chipStyle } from './reportHelpers';

/**
 * Transcripts page -- recent transcripts list at the top + upload form
 * below.
 *
 * Upload posts {title, rawText} to /api/transcripts (Track E backend).
 * The endpoint returns 202 Accepted; the orchestrator runs async (~30-90s)
 * and the result lands in the Cosmos `transcripts` container, which the
 * GET /api/transcripts list will reflect on next refresh.
 */
const TranscriptUpload = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const { transcripts, loading: listLoading, error: listError, reload: reloadList } = useTranscripts(30);

  const charCount = rawText.length;
  const wordCount = rawText.trim() ? rawText.trim().split(/\s+/).length : 0;
  const canSubmit = title.trim().length > 0 && rawText.trim().length > 0 && !submitting;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiCall(API_CONFIG.endpoints.transcripts, {
        method: 'POST',
        body: JSON.stringify({ title: title.trim(), rawText }),
      });
      setResult(response);
      setTitle('');
      setRawText('');
      // The orchestrator runs ~30-90s; the new transcript won't be in the
      // list yet on immediate reload. Still useful to invalidate so a later
      // navigation re-fetches.
      invalidateTranscriptsCache();
    } catch (err) {
      console.error('Transcript upload failed', err);
      setError(err.message || 'Upload failed -- check the function key in Settings.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Transcripts</h1>
          <p>Browse ingested transcripts and queue new ones.</p>
        </div>
      </div>

      {/* Recent transcripts list */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>
            Recent transcripts
            {!listLoading && transcripts.length > 0 && (
              <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                ({transcripts.length})
              </span>
            )}
          </h2>
          <button
            className="btn btn-secondary"
            onClick={reloadList}
            disabled={listLoading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
          >
            <RefreshCw size={14} className={listLoading ? 'spin' : ''} />
            Refresh
          </button>
        </div>

        {listLoading && transcripts.length === 0 ? (
          <div className="empty-state">
            <Loader2 size={32} className="spin" />
            <p>Loading transcripts...</p>
          </div>
        ) : listError ? (
          <div className="empty-state">
            <AlertCircle size={32} />
            <p>{listError}</p>
          </div>
        ) : transcripts.length === 0 ? (
          <div className="empty-state">
            <FileText size={32} />
            <p>No transcripts yet. Use the form below to upload your first one.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {transcripts.map((t) => (
              <TranscriptListRow
                key={t.id}
                transcript={t}
                onClick={() => navigate(`/transcripts/${encodeURIComponent(t.id)}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload form */}
      <h2 style={{ fontSize: '1rem', marginTop: 0, marginBottom: '0.75rem' }}>Upload new transcript</h2>

      {result && (
        <div className="card" style={{
          background: 'var(--severity-info-bg)',
          borderLeft: '4px solid var(--severity-info-border)',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <CheckCircle2 size={24} style={{ color: 'var(--primary-color)', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1rem' }}>
                Transcript queued for ingestion
              </h3>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <div><strong>ID:</strong> <code style={{ wordBreak: 'break-all' }}>{result.transcriptId}</code></div>
                {result.title && <div><strong>Title:</strong> {result.title}</div>}
                {result.uploadedAt && <div><strong>Uploaded at:</strong> {result.uploadedAt}</div>}
                {result.statusHint && (
                  <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                    {result.statusHint}
                  </div>
                )}
              </div>
              <button
                onClick={() => { setResult(null); reloadList(); }}
                className="btn btn-secondary"
                style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
              >
                <RefreshCw size={14} /> Refresh list
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{
          background: 'var(--severity-alert-bg)',
          borderLeft: '4px solid var(--severity-alert-border)',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <AlertCircle size={20} style={{ color: 'var(--danger-color)', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong>Upload failed:</strong> {error}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card">
        <div style={{ marginBottom: '1.25rem' }}>
          <label
            htmlFor="transcript-title"
            style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}
          >
            Title <span style={{ color: 'var(--danger-color)' }}>*</span>
          </label>
          <input
            id="transcript-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Aug 13 webinar -- AI infrastructure update"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '0.625rem 0.75rem',
              fontSize: '0.9375rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="transcript-body"
            style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}
          >
            Raw transcript <span style={{ color: 'var(--danger-color)' }}>*</span>
          </label>
          <textarea
            id="transcript-body"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste the full raw transcript here. YouTube auto-captions, Otter, Rev -- any source is fine. Speaker labels and timestamps will be normalized server-side."
            disabled={submitting}
            rows={20}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '0.875rem',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              lineHeight: 1.5,
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.8125rem',
            color: 'var(--text-secondary)',
            marginTop: '0.375rem',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
            <span>{wordCount.toLocaleString()} words / {charCount.toLocaleString()} chars</span>
            <span>
              <FileText size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
              Aim for the full webinar text -- the backend chunks long inputs automatically.
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="spin" />
                Queuing...
              </>
            ) : (
              <>
                <Send size={16} />
                Queue ingestion
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={submitting || (!title && !rawText)}
            onClick={() => { setTitle(''); setRawText(''); setError(null); setResult(null); }}
          >
            Clear
          </button>
        </div>
      </form>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

/**
 * Single row in the recent-transcripts list. Renders metadata + chips for
 * extracted tickers/themes. Kept compact -- the full transcript content
 * isn't surfaced here (no detail view yet, deliberately scope-bounded;
 * the list payload is also lightweight by design).
 */
const TranscriptListRow = ({ transcript, onClick }) => {
  const date = transcript.uploadedAt ? parseISO(transcript.uploadedAt) : null;
  const tickers = (transcript.extractedTickers || []).slice(0, 6);
  const themes = (transcript.extractedThemes || []).slice(0, 4);
  const moreTickers = Math.max(0, (transcript.extractedTickers?.length || 0) - tickers.length);
  const moreThemes = Math.max(0, (transcript.extractedThemes?.length || 0) - themes.length);

  return (
    <button
      type="button"
      onClick={onClick}
      className="analysis-list-card"
      style={{
        padding: '0.875rem',
        background: 'var(--bg-secondary, #f8f9fa)',
        borderRadius: '6px',
        borderLeft: '3px solid var(--primary-color)',
        border: '1px solid var(--border-color)',
        borderLeftWidth: '3px',
        textAlign: 'left',
        width: '100%',
        cursor: 'pointer',
      }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.75rem',
        marginBottom: '0.5rem',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
            {transcript.title || '(untitled transcript)'}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
            {date && format(date, 'MMM dd, yyyy')}
            {transcript.chunkCount > 0 && ` • ${transcript.chunkCount} chunk${transcript.chunkCount === 1 ? '' : 's'}`}
            {!transcript.analyzedAt && ' • analyzing...'}
          </div>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
      </div>

      {transcript.summary && (
        <p style={{
          margin: '0.5rem 0 0.75rem',
          fontSize: '0.875rem',
          color: 'var(--text-primary)',
          lineHeight: 1.5,
          // Clamp to ~3 lines for list density.
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {transcript.summary}
        </p>
      )}

      {(tickers.length > 0 || themes.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
          {tickers.map((t) => (
            <span key={`tk-${t}`} style={chipStyle('blue')}>{t}</span>
          ))}
          {moreTickers > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.125rem 0.25rem' }}>
              +{moreTickers}
            </span>
          )}
          {themes.map((th) => (
            <span key={`th-${th}`} style={chipStyle('purple')}>{th}</span>
          ))}
          {moreThemes > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.125rem 0.25rem' }}>
              +{moreThemes} themes
            </span>
          )}
        </div>
      )}
    </button>
  );
};

export default TranscriptUpload;
